/**
 * @file index.tsx
 *
 * @fileoverview Component for showing a grid of products in a landing page row.
 */
import React from 'react';
import { Paragraph, ProductsGridParagraph } from '~/jobiqo/types/types';
import ProductBoxWrapper from '~/jobiqo/components/Landingpage/Products/components/ProductBoxWrapper';
import { Slide } from '~/vcl/molecules/15-slider';
import styles from './products.module.scss';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useIsMobile } from '~/vcl/hooks/useIsMobile';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface LandingPageProductsGridProps {
  rowProductsGridInfo: ProductsGridParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

/**
 * Get the size of the grid element based on the container size.
 */
function getElementSize(containerSize: string) {
  switch (containerSize) {
    case 'small':
      return '250px';
    case 'medium':
      return '300px';
    case 'large':
      return '350px';
    case 'xLarge':
      return '450px';
    default:
      return '350px';
  }
}

export const LandingPageProductsGrid = ({
  rowProductsGridInfo,
  maxContainerSize,
}: LandingPageProductsGridProps) => {
  const isMobile = useIsMobile();
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const products: React.ReactNode[] =
    rowProductsGridInfo.items && rowProductsGridInfo.items.length > 0
      ? rowProductsGridInfo.items
          .filter((p) => !!p)
          .map((product, index) => {
            return (
              <ProductBoxWrapper key={index} product={product as Paragraph} />
            );
          })
      : [];

  const gridElementMaxSize = getElementSize(maxContainerSize ?? 'medium');
  const SHOW_DOTS = frontendTheme.LANDING_PAGES.PRODUCTS.SHOW_DOTS;

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      padding={0}
      backgroundColorOverride={
        rowProductsGridInfo.backgroundColor
          ? `var(--color-${rowProductsGridInfo.backgroundColor})`
          : 'transparent'
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <Spacing
          paddingTop={rowProductsGridInfo.spacing ?? 11}
          paddingBottom={rowProductsGridInfo.spacing ?? 12}
          margin={0}
        >
          {rowProductsGridInfo.productsSubtitle && (
            <>
              <Text
                marginTop="0"
                textAlign="center"
                color={rowProductsGridInfo.color as any}
                fontWeight="bold"
                textTransform="uppercase"
              >
                {rowProductsGridInfo.productsSubtitle}
              </Text>
              <Spacing size="medium" />
            </>
          )}
          {rowProductsGridInfo.productsTitle && (
            <>
              <Text
                textAlign="center"
                tag="h2"
                fontSize={[5, 7]}
                lineHeight={[5, 7]}
              >
                {rowProductsGridInfo.productsTitle}
              </Text>
              <Spacing size="large" />
            </>
          )}
          {rowProductsGridInfo.text && (
            <>
              <Text
                textAlign="center"
                color="gray6"
                fontSize={4}
                lineHeight={5}
              >
                {rowProductsGridInfo.text}
              </Text>
              <Spacing marginBottom={11} />
            </>
          )}
          <Spacing marginBottom={11} />
          {rowProductsGridInfo.gridDisplayOptions === 'scroll_horizontally' &&
          (products.length > 3 || isMobile) ? (
            <Slide
              slides={products.map((product, index) => (
                <Spacing
                  key={index}
                  marginRight={4}
                  marginLeft={4}
                  marginTop={0}
                  marginBottom={0}
                >
                  {product}
                </Spacing>
              ))}
              slidesToShow={3}
              dots={SHOW_DOTS}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: 2,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 1,
                  },
                },
              ]}
            />
          ) : (
            <Grid
              className={styles.productsGrid}
              justifyContent="center"
              gridTemplateColumns={`repeat(auto-fit, ${gridElementMaxSize})`}
            >
              {products.map((product, index) => {
                return (
                  <Cell
                    display="grid"
                    justifySelf="center"
                    key={index}
                    className="vcl-full-width"
                  >
                    {product}
                  </Cell>
                );
              })}
            </Grid>
          )}
        </Spacing>
      </Main>
    </Box>
  );
};
export default LandingPageProductsGrid;
